// Public customer auth pages:
//   /login, /signup, /forgot-password, /reset-password?token=...
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft, CheckCircle2, Eye, EyeOff, AlertCircle, Phone } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { notifyOdooLogin } from "@/api/odooLoginBridge";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";

const STR = {
  en: {
    login: "Log in",
    signup: "Sign up",
    forgot: "Forgot password?",
    reset: "Reset password",
    email: "Email",
    password: "Password",
    confirm: "Confirm password",
    name: "Full name",
    submit_login: "Log in",
    submit_signup: "Create account",
    submit_forgot: "Send reset link",
    submit_reset: "Set new password",
    no_account: "Don't have an account?",
    have_account: "Already have an account?",
    back_to_login: "Back to log in",
    remember_pwd: "Remember it now?",
    forgot_sent: "If an account with that email exists, a reset link has been sent. Check your inbox (and spam).",
    reset_ok: "Password reset successful. You can now log in.",
    pwd_mismatch: "Passwords don't match.",
    pwd_min: "Password must be at least 6 characters.",
    login_failed: "Login failed. Please try again.",
    account_not_found: "No account found for this email or mobile number.",
    invalid_password: "Incorrect password.",
    email_not_verified: "Please verify your email first.",
    account_disabled: "This account is currently inactive.",
    account_frozen: "This account is frozen. Please contact BOB support.",
    account_deleted: "This account has been deleted. You can create a new account with the same email.",
    session_expired: "Your session ended. Please log in again.",
    missing_login_fields: "Please enter your email or mobile number and password.",
    invalid_email: "Please enter a valid email address.",
    too_many_attempts: "Too many login attempts. Please wait one hour or reset your password.",
    signup_failed: "Sign up failed. Please try again.",
    missing_token: "This reset link is invalid or missing. Please request a new one.",
    pwd_requirements: "Password must be at least 8 characters and include at least one uppercase letter and one special character, such as @ # $",
    pwd_len_check: "At least 8 characters",
    pwd_upper_check: "One uppercase letter A-Z",
    pwd_special_check: "One special character",
    network_error: "We could not connect to the server. Please check your connection and try again.",
    reset_failed: "We could not reset your password. Please try again.",
    show_password: "Show password",
    hide_password: "Hide password",
    terms: "Terms",
    privacy: "Privacy",
    prohibited: "Prohibited Items",
    login_intro: "Welcome back to BOB Services LB.",
    signup_intro: "Create your BOB Services account in seconds.",
    forgot_intro: "Enter your email and we'll send you a secure reset link.",
    reset_intro: "Choose a new password for your account.",
    secure_access: "Secure customer access",
    secure_footer: "Premium shopping, shipping, and account access for BOB Services LB.",
    email_or_phone: "Email or mobile",
    email_or_phone_placeholder: "email@example.com or +9617…",
    phone: "Mobile number",
    phone_placeholder: "+961 7X XXX XXX",
    phone_hint: "Use the same mobile saved on your BOB account.",
    invalid_phone: "Please enter a valid mobile number with country code.",
  },
  ar: {
    login: "تسجيل الدخول",
    signup: "إنشاء حساب",
    forgot: "نسيت كلمة المرور؟",
    reset: "إعادة تعيين كلمة المرور",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    confirm: "تأكيد كلمة المرور",
    name: "الاسم الكامل",
    submit_login: "تسجيل الدخول",
    submit_signup: "إنشاء الحساب",
    submit_forgot: "إرسال رابط الإعادة",
    submit_reset: "تعيين كلمة المرور الجديدة",
    no_account: "ليس لديك حساب؟",
    have_account: "لديك حساب بالفعل؟",
    back_to_login: "العودة لتسجيل الدخول",
    remember_pwd: "تذكّرتها؟",
    forgot_sent: "إذا كان هناك حساب مرتبط بهذا البريد، فقد أرسلنا رابط إعادة التعيين. تفقد صندوق الوارد وSpam.",
    reset_ok: "تم تعيين كلمة المرور بنجاح. يمكنك الآن تسجيل الدخول.",
    pwd_mismatch: "كلمتا المرور غير متطابقتين.",
    pwd_min: "كلمة المرور يجب أن تكون 6 أحرف على الأقل.",
    login_failed: "تعذر تسجيل الدخول. يرجى المحاولة مرة أخرى.",
    account_not_found: "لا يوجد حساب بهذا البريد أو رقم الموبايل.",
    invalid_password: "كلمة المرور غير صحيحة.",
    email_not_verified: "يرجى تفعيل بريدك الإلكتروني أولاً.",
    account_disabled: "هذا الحساب غير مفعل حالياً.",
    account_frozen: "هذا الحساب مجمّد. يرجى التواصل مع دعم BOB.",
    account_deleted: "تم حذف هذا الحساب. يمكنك إنشاء حساب جديد بالبريد نفسه.",
    session_expired: "انتهت جلستك. يرجى تسجيل الدخول مجدداً.",
    missing_login_fields: "يرجى إدخال البريد أو رقم الموبايل وكلمة المرور.",
    invalid_email: "يرجى إدخال بريد إلكتروني صحيح.",
    too_many_attempts: "تم تجاوز عدد محاولات تسجيل الدخول. يرجى الانتظار ساعة أو إعادة تعيين كلمة المرور.",
    signup_failed: "تعذر إنشاء الحساب. يرجى المحاولة مرة أخرى.",
    missing_token: "رابط إعادة التعيين غير صالح أو مفقود",
    pwd_requirements: "يجب أن تكون كلمة المرور 8 أحرف على الأقل، وتحتوي على حرف كبير واحد على الأقل ورمز خاص مثل @ # $",
    pwd_len_check: "8 أحرف على الأقل",
    pwd_upper_check: "حرف كبير واحد A-Z",
    pwd_special_check: "رمز خاص واحد",
    network_error: "تعذر الاتصال بالخادم. يرجى التأكد من الاتصال والمحاولة مرة أخرى.",
    reset_failed: "تعذر إعادة تعيين كلمة المرور. يرجى المحاولة مرة أخرى.",
    show_password: "إظهار كلمة المرور",
    hide_password: "إخفاء كلمة المرور",
    terms: "الشروط",
    privacy: "الخصوصية",
    prohibited: "الممنوعات",
    login_intro: "أهلًا بعودتك إلى BOB Services LB.",
    signup_intro: "أنشئ حسابك في BOB Services خلال ثوانٍ.",
    forgot_intro: "أدخل بريدك الإلكتروني وسنرسل لك رابطًا آمنًا لإعادة التعيين.",
    reset_intro: "اختر كلمة مرور جديدة لحسابك.",
    secure_access: "دخول آمن للعملاء",
    secure_footer: "تجربة حساب وشحن وطلب مميزة من BOB Services LB.",
    email_or_phone: "البريد أو الموبايل",
    email_or_phone_placeholder: "email@example.com أو +9617…",
    phone: "رقم الموبايل",
    phone_placeholder: "+961 7X XXX XXX",
    phone_hint: "استخدم نفس الرقم المحفوظ في حساب BOB.",
    invalid_phone: "يرجى إدخال رقم موبايل صحيح مع رمز الدولة.",
  },
  fr: {
    login: "Connexion",
    signup: "Créer un compte",
    forgot: "Mot de passe oublié ?",
    reset: "Réinitialiser le mot de passe",
    email: "E-mail",
    password: "Mot de passe",
    confirm: "Confirmer le mot de passe",
    name: "Nom complet",
    submit_login: "Se connecter",
    submit_signup: "Créer mon compte",
    submit_forgot: "Envoyer le lien",
    submit_reset: "Définir le nouveau mot de passe",
    no_account: "Pas encore de compte ?",
    have_account: "Vous avez déjà un compte ?",
    back_to_login: "Retour à la connexion",
    remember_pwd: "Vous vous en souvenez ?",
    forgot_sent: "Si un compte est associé à cet e-mail, un lien de réinitialisation a été envoyé. Vérifiez votre boîte de réception (et les spams).",
    reset_ok: "Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.",
    pwd_mismatch: "Les mots de passe ne correspondent pas.",
    pwd_min: "Le mot de passe doit contenir au moins 6 caractères.",
    login_failed: "Connexion impossible. Veuillez réessayer.",
    account_not_found: "Aucun compte trouvé pour cet e-mail ou ce mobile.",
    invalid_password: "Mot de passe incorrect.",
    email_not_verified: "Veuillez d'abord vérifier votre e-mail.",
    account_disabled: "Ce compte est actuellement inactif.",
    account_frozen: "Ce compte est gelé. Veuillez contacter l'assistance BOB.",
    account_deleted: "Ce compte a été supprimé. Vous pouvez créer un nouveau compte avec la même adresse e-mail.",
    session_expired: "Votre session est terminée. Veuillez vous reconnecter.",
    missing_login_fields: "Veuillez saisir votre e-mail ou mobile et votre mot de passe.",
    invalid_email: "Veuillez saisir une adresse e-mail valide.",
    too_many_attempts: "Trop de tentatives de connexion. Veuillez attendre une heure avant de rÃ©essayer.",
    signup_failed: "Création du compte impossible. Veuillez réessayer.",
    missing_token: "Ce lien de réinitialisation est invalide ou a expiré. Veuillez en demander un nouveau.",
    pwd_requirements: "Le mot de passe doit contenir au moins 8 caractères, une majuscule et un caractère spécial, comme @ # $",
    pwd_len_check: "Au moins 8 caractères",
    pwd_upper_check: "Une majuscule A-Z",
    pwd_special_check: "Un caractère spécial",
    network_error: "Impossible de se connecter au serveur. Vérifiez votre connexion puis réessayez.",
    reset_failed: "Impossible de réinitialiser votre mot de passe. Veuillez réessayer.",
    show_password: "Afficher le mot de passe",
    hide_password: "Masquer le mot de passe",
    terms: "Conditions",
    privacy: "Confidentialité",
    prohibited: "Articles interdits",
    login_intro: "Heureux de vous revoir sur BOB Services LB.",
    signup_intro: "Créez votre compte BOB Services en quelques secondes.",
    forgot_intro: "Entrez votre e-mail et nous vous enverrons un lien sécurisé.",
    reset_intro: "Choisissez un nouveau mot de passe pour votre compte.",
    secure_access: "Accès client sécurisé",
    secure_footer: "Accès premium à vos commandes, expéditions et services BOB Services LB.",
    email_or_phone: "E-mail ou mobile",
    email_or_phone_placeholder: "email@example.com ou +9617…",
    phone: "Numéro de mobile",
    phone_placeholder: "+961 7X XXX XXX",
    phone_hint: "Utilisez le même mobile enregistré sur votre compte BOB.",
    invalid_phone: "Veuillez saisir un numéro valide avec indicatif pays.",
  },
};

const EXTRA_STR = {
  en: {
    verify_email: "Verify your email",
    verification_code: "Verification code",
    submit_verify: "Verify email",
    resend_code: "Resend code",
    back_to_signup: "Back to sign up",
    verify_sent: "We sent a 6-digit verification code to your email.",
    verify_send_failed: "We created your account, but we could not send the verification code right now. Please try resending it.",
    verify_success: "Your email has been verified.",
    verify_failed: "We could not verify your code. Please try again.",
    verify_code_invalid: "The verification code is incorrect or has expired.",
    verify_code_expired: "This verification code has expired. Please request a new one.",
    verify_code_format: "Please enter the 6-digit verification code.",
    verify_required_login: "Please verify your email before logging in.",
    resend_wait: "Please wait a moment before requesting a new code.",
    verification_pending: "Your account is almost ready.",
    verification_email_to: "Verification code sent to",
    verify_intro: "Enter the 6-digit code we sent to your email to activate your account.",
  },
  ar: {
    verify_email: "تحقق من بريدك الإلكتروني",
    verification_code: "رمز التحقق",
    submit_verify: "تأكيد البريد الإلكتروني",
    resend_code: "إعادة إرسال الرمز",
    back_to_signup: "العودة لإنشاء الحساب",
    verify_sent: "أرسلنا رمز تحقق مكوّنًا من 6 أرقام إلى بريدك الإلكتروني.",
    verify_send_failed: "تم إنشاء الحساب، لكن تعذر إرسال رمز التحقق الآن. يرجى محاولة إعادة الإرسال.",
    verify_success: "تم التحقق من بريدك الإلكتروني بنجاح.",
    verify_failed: "تعذر التحقق من الرمز. يرجى المحاولة مرة أخرى.",
    verify_code_invalid: "رمز التحقق غير صحيح أو منتهي الصلاحية.",
    verify_code_expired: "انتهت صلاحية رمز التحقق. يرجى طلب رمز جديد.",
    verify_code_format: "يرجى إدخال رمز التحقق المكوّن من 6 أرقام.",
    verify_required_login: "يرجى التحقق من بريدك الإلكتروني قبل تسجيل الدخول.",
    resend_wait: "يرجى الانتظار قليلًا قبل طلب رمز جديد.",
    verification_pending: "حسابك جاهز تقريبًا.",
    verification_email_to: "تم إرسال رمز التحقق إلى",
    verify_intro: "أدخل رمز التحقق المكوّن من 6 أرقام الذي أرسلناه إلى بريدك الإلكتروني لتفعيل حسابك.",
  },
  fr: {
    verify_email: "Vérifiez votre e-mail",
    verification_code: "Code de vérification",
    submit_verify: "Vérifier l'e-mail",
    resend_code: "Renvoyer le code",
    back_to_signup: "Retour à l'inscription",
    verify_sent: "Nous avons envoyé un code de vérification à 6 chiffres à votre e-mail.",
    verify_send_failed: "Votre compte a bien été créé, mais nous n'avons pas pu envoyer le code de vérification pour le moment. Veuillez réessayer.",
    verify_success: "Votre e-mail a été vérifié.",
    verify_failed: "Nous n'avons pas pu vérifier votre code. Veuillez réessayer.",
    verify_code_invalid: "Le code de vérification est incorrect ou expiré.",
    verify_code_expired: "Ce code de vérification a expiré. Veuillez en demander un nouveau.",
    verify_code_format: "Veuillez saisir le code de vérification à 6 chiffres.",
    verify_required_login: "Veuillez vérifier votre e-mail avant de vous connecter.",
    resend_wait: "Veuillez patienter un instant avant de demander un nouveau code.",
    verification_pending: "Votre compte est presque prêt.",
    verification_email_to: "Code envoyé à",
    verify_intro: "Saisissez le code à 6 chiffres envoyé à votre e-mail pour activer votre compte.",
  },
};

function useT() {
  const { lang } = useLanguage();
  return { ...STR.en, ...(STR[lang] || STR.en), ...EXTRA_STR.en, ...(EXTRA_STR[lang] || EXTRA_STR.en) };
}

const resetPasswordChecks = (value) => ({
  length: value.length >= 8,
  uppercase: /[A-Z]/.test(value),
  special: /[^A-Za-z0-9]/.test(value),
});

const allPasswordChecksPass = (checks) => Object.values(checks).every(Boolean);

const isNetworkError = (message = "") =>
  /load failed|failed to fetch|networkerror|network request failed|fetch failed/i.test(message);

const isTechnicalClientError = (message = "") =>
  /body stream already read|failed to execute 'json' on 'response'|unexpected token|unexpected end of json input|json\.parse|invalid json/i.test(message);

const readAuthResponse = async (res) => {
  const raw = await res.text().catch(() => "");
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { detail: raw };
  }
};

class AuthApiError extends Error {
  constructor(message, data = {}) {
    super(message);
    this.name = "AuthApiError";
    this.data = data;
  }
}

const requestAuthPayload = async (res, formatApiDetail, fallback) => {
  const data = await readAuthResponse(res);
  if (!res.ok) {
    const detail = data?.detail ? formatApiDetail(data.detail) : fallback;
    throw new AuthApiError(detail || fallback, data);
  }
  return data;
};

const mapAuthErrorCode = (errorCode, t) => {
  const code = String(errorCode || "").toUpperCase();
  switch (code) {
    case "ACCOUNT_NOT_FOUND":
      return t.account_not_found;
    case "INVALID_PASSWORD":
      return t.invalid_password;
    case "EMAIL_NOT_VERIFIED":
    case "EMAIL_VERIFICATION_REQUIRED":
      return t.verify_required_login;
    case "ACCOUNT_DISABLED":
      return t.account_disabled;
    case "ACCOUNT_FROZEN":
      return t.account_frozen;
    case "ACCOUNT_DELETED":
      return t.account_deleted;
    case "MISSING_FIELDS":
      return t.missing_login_fields;
    case "INVALID_EMAIL":
      return t.invalid_email;
    case "TOO_MANY_ATTEMPTS":
      return t.too_many_attempts;
    case "NETWORK_ERROR":
      return t.network_error;
    case "INVALID_PHONE":
      return t.invalid_phone;
    case "VERIFICATION_CODE_INVALID":
      return t.verify_code_invalid;
    case "VERIFICATION_CODE_EXPIRED":
      return t.verify_code_expired;
    case "VERIFICATION_RESEND_COOLDOWN":
      return t.resend_wait;
    case "VERIFICATION_EMAIL_SEND_FAILED":
      return t.verify_send_failed;
    default:
      return "";
  }
};

const friendlyAuthError = (err, t, fallback) => {
  const mappedError = mapAuthErrorCode(err?.data?.error_code, t);
  if (mappedError) return mappedError;
  const message = err?.message || "";
  if (isNetworkError(message)) return t.network_error;
  if (isTechnicalClientError(message)) return fallback;
  return message || fallback;
};

function AuthShell({ title, intro, children, footer, testid }) {
  const t = useT();

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(243,112,33,0.22),_transparent_28%),linear-gradient(160deg,#07111f_0%,#0a1628_48%,#102647_100%)] px-4 py-10 sm:py-14 dark:bg-[radial-gradient(circle_at_top,_rgba(243,112,33,0.16),_transparent_24%),linear-gradient(160deg,#050505_0%,#0A0A0A_52%,#111111_100%)]"
      data-testid={testid}
    >
      <div className="w-full max-w-lg">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/6 px-4 py-2 text-sm font-medium text-white/80 backdrop-blur transition-all hover:border-white/20 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/20"
        >
          <ArrowLeft className="h-4 w-4" />
          BOB Services LB
        </Link>

        <div className="overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(4,14,28,0.22)] dark:border-white/10 dark:bg-[#111111]">
          <div className="bg-[linear-gradient(135deg,rgba(243,112,33,0.16),rgba(10,22,40,0.02))] px-6 py-6 sm:px-8 dark:bg-[linear-gradient(135deg,rgba(243,112,33,0.12),rgba(17,17,17,0.7))]">
            <div className="inline-flex items-center rounded-full border border-[#F37021]/18 bg-[#F37021]/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C75A14] dark:bg-[#1A1A1A] dark:text-[#F8A056]">
              {t.secure_access}
            </div>
            <h1 className="mt-4 text-[30px] font-bold leading-tight text-[#0a1628] sm:text-[34px] dark:text-white">{title}</h1>
            {intro && <p className="mt-3 max-w-md text-sm leading-6 text-[#5f6774] sm:text-[15px] dark:text-[#D4D4D4]">{intro}</p>}
          </div>

          <div className="p-6 sm:p-8">
            <div className="space-y-4">{children}</div>

            {footer && (
              <div className="mt-7 border-t border-black/6 pt-5 text-center text-sm leading-6 text-[#666] dark:border-white/10 dark:text-[#D4D4D4]">
                {footer}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] font-medium text-[#7b8190] dark:text-[#A3A3A3]">
              <Link to="/terms" className="transition-colors hover:text-[#F37021] hover:underline">{t.terms}</Link>
              <span aria-hidden>&bull;</span>
              <Link to="/privacy" className="transition-colors hover:text-[#F37021] hover:underline">{t.privacy}</Link>
              <span aria-hidden>&bull;</span>
              <Link to="/prohibited-items" className="transition-colors hover:text-[#F37021] hover:underline">{t.prohibited}</Link>
            </div>
          </div>

          <div className="border-t border-black/6 bg-[#fbf6f1] px-6 py-3 text-center text-[11px] font-medium text-[#6d635d] sm:px-8 dark:border-white/10 dark:bg-[#101010] dark:text-[#A3A3A3]">
            {t.secure_footer}
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ icon: Icon, label, hint, inputClassName = "", ...props }) {
  return (
    <div className="space-y-2">
      {label && <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5d6674] dark:text-[#D4D4D4]">{label}</label>}
      <div className="relative">
        {Icon && <Icon className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b94a3]" />}
        <input
          {...props}
          className={`h-[52px] w-full rounded-2xl border border-[#d9dee7] bg-[#fbfcfe] ${Icon ? "ps-11" : "px-4"} pe-4 text-sm text-[#0a1628] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-all focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-white dark:shadow-none ${inputClassName}`}
        />
      </div>
      {hint && <p className="text-xs leading-5 text-[#70798a] dark:text-[#A3A3A3]">{hint}</p>}
    </div>
  );
}

function PasswordInput({ icon: Icon, label, hint, showLabel, hideLabel, toggleTestId, inputClassName = "", ...props }) {
  const [visible, setVisible] = useState(false);
  const ToggleIcon = visible ? EyeOff : Eye;

  return (
    <div className="space-y-2">
      {label && <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5d6674] dark:text-[#D4D4D4]">{label}</label>}
      <div className="relative">
        {Icon && <Icon className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b94a3]" />}
        <input
          {...props}
          type={visible ? "text" : "password"}
          className={`h-[52px] w-full rounded-2xl border border-[#d9dee7] bg-[#fbfcfe] ${Icon ? "ps-11" : "px-4"} pe-12 text-sm text-[#0a1628] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-all focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 dark:border-white/10 dark:bg-[#121212] dark:text-white dark:shadow-none ${inputClassName}`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute end-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-[#777] transition-colors hover:bg-[#F37021]/10 hover:text-[#F37021] focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:text-[#A3A3A3]"
          aria-label={visible ? hideLabel : showLabel}
          data-testid={toggleTestId}
        >
          <ToggleIcon className="h-4 w-4" />
        </button>
      </div>
      {hint && <p className="text-xs leading-5 text-[#70798a] dark:text-[#A3A3A3]">{hint}</p>}
    </div>
  );
}

function PrimaryBtn({ loading, disabled, children, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      {...props}
      className="h-12 w-full rounded-2xl bg-[#F37021] text-sm font-semibold text-white shadow-[0_14px_30px_rgba(243,112,33,0.24)] transition-all hover:-translate-y-0.5 hover:bg-[#d85f15] hover:shadow-[0_18px_34px_rgba(243,112,33,0.28)] focus:outline-none focus:ring-4 focus:ring-[#F37021]/20 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {loading ? "..." : children}
    </button>
  );
}

function VerificationStep({ email, initialNotice, onBack, backLabel, backHref }) {
  const t = useT();
  const navigate = useNavigate();
  const { apiPublic, formatApiDetail, setSession } = useCustomerAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [notice, setNotice] = useState(initialNotice || t.verify_sent);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    setNotice(initialNotice || t.verify_sent);
  }, [initialNotice, t.verify_sent]);

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
  const canVerify = Boolean(email) && normalizedCode.length === 6 && !loading;

  const onVerify = async (e) => {
    e.preventDefault();
    if (normalizedCode.length !== 6) {
      setInlineError(t.verify_code_format);
      return;
    }
    setInlineError("");
    setLoading(true);
    try {
      const res = await apiPublic("/verify-email-code", {
        method: "POST",
        body: JSON.stringify({ email, code: normalizedCode }),
      });
      const data = await requestAuthPayload(res, formatApiDetail, t.verify_failed);
      if (data?.token && data?.customer) {
        setSession(data.token, data.customer);
      }
      trackEvent("email_verification_success", {
        routeName: "verify_email",
        customerId: data?.customer?.id,
        metadata: { source: "verification_step" },
      });
      toast.success(t.verify_success);
      navigate("/account");
    } catch (err) {
      const message = friendlyAuthError(err, t, t.verify_failed);
      setInlineError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (!email || cooldown > 0) return;
    setInlineError("");
    setResending(true);
    try {
      const res = await apiPublic("/resend-verification-code", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      await requestAuthPayload(res, formatApiDetail, t.verify_send_failed);
      trackEvent("email_verification_requested", {
        routeName: "verify_email",
        metadata: { source: "resend_code" },
      });
      setNotice(t.verify_sent);
      setCooldown(60);
      toast.success(t.verify_sent);
    } catch (err) {
      const message = friendlyAuthError(err, t, t.verify_send_failed);
      setInlineError(message);
      if (err?.data?.error_code === "verification_resend_cooldown") {
        setCooldown(60);
      }
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[28px] border border-[#F37021]/16 bg-[linear-gradient(180deg,#fffaf5_0%,#fff4ec_100%)] px-5 py-6 text-center shadow-[0_16px_36px_rgba(16,24,40,0.08)] dark:border-[#F37021]/20 dark:bg-[linear-gradient(180deg,#111111_0%,#0A0A0A_100%)]">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F37021]/12 text-[#F37021]">
          <Mail className="h-7 w-7" />
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9b6c49] dark:text-[#F8A056]">{t.verification_pending}</p>
        <p className="mt-3 text-sm leading-7 text-[#445064] dark:text-[#D4D4D4]">{notice}</p>
        <p className="mt-2 text-xs text-[#6b7280] dark:text-[#A3A3A3]">
          {t.verification_email_to} <span className="font-semibold text-[#0a1628] dark:text-white">{email}</span>
        </p>
      </div>

      <form onSubmit={onVerify} className="space-y-4">
        <Input
          icon={Lock}
          label={t.verification_code}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={normalizedCode}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            if (inlineError) setInlineError("");
          }}
          placeholder="000000"
          data-testid="verify-code-input"
          hint={t.verify_intro}
        />

        {inlineError && (
          <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-relaxed text-red-700" data-testid="verify-inline-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{inlineError}</span>
          </div>
        )}

        <PrimaryBtn loading={loading} disabled={!canVerify} data-testid="verify-submit">
          {t.submit_verify}
        </PrimaryBtn>
      </form>

      <button
        type="button"
        onClick={onResend}
        disabled={resending || cooldown > 0}
        className="w-full rounded-2xl border border-[#F37021]/18 bg-[#fff7f1] px-4 py-3 text-sm font-semibold text-[#C75A14] transition-colors hover:bg-[#fff1e7] focus:outline-none focus:ring-4 focus:ring-[#F37021]/12 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#F37021]/20 dark:bg-[#151515] dark:text-[#F8A056]"
        data-testid="verify-resend"
      >
        {cooldown > 0 ? `${t.resend_code} (${cooldown}s)` : resending ? "..." : t.resend_code}
      </button>

      {(onBack || backHref) && (
        <div className="text-center text-sm">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="font-medium text-[#F37021] hover:underline"
            >
              {backLabel}
            </button>
          ) : (
            <Link to={backHref} className="font-medium text-[#F37021] hover:underline">
              {backLabel}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const { apiPublic, setSession, formatApiDetail, sessionErrorCode } = useCustomerAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationNotice, setVerificationNotice] = useState("");
  const [inlineError, setInlineError] = useState("");

  useEffect(() => {
    trackEvent("login_view", { routeName: "login" });
  }, []);

  useEffect(() => {
    const code = String(sessionErrorCode || "").toLowerCase();
    if (code === "account_frozen" || code === "account_blocked") setInlineError(t.account_frozen);
    else if (code === "account_deleted") setInlineError(t.account_deleted);
    else if (code === "account_disabled") setInlineError(t.account_disabled);
    else if (code === "session_expired" || code === "session_invalidated") setInlineError(t.session_expired);
  }, [sessionErrorCode, t.account_deleted, t.account_disabled, t.account_frozen, t.session_expired]);

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setInlineError("");
    setLoading(true);
    try {
      const identifier = email.trim();
      const payload = identifier.includes("@")
        ? { email: identifier, password }
        : { phone: identifier, password };
      const res = await apiPublic("/login", { method: "POST", body: JSON.stringify(payload) });
      const data = await requestAuthPayload(res, formatApiDetail, t.login_failed);
      trackEvent("customer_login_success", {
        routeName: "login",
        customerId: data?.customer?.id,
        metadata: { method: identifier.includes("@") ? "email_password" : "phone_password" },
      });
      setSession(data.token, data.customer);
      if (identifier.includes("@")) {
        void notifyOdooLogin({ email: identifier, password });
      } else if (data?.customer?.email) {
        void notifyOdooLogin({ email: data.customer.email, password });
      }
      toast.success("Welcome back");
      navigate("/account");
    } catch (err) {
      const message = friendlyAuthError(err, t, t.login_failed);
      setInlineError(message);
      trackEvent("customer_login_failed", {
        routeName: "login",
        metadata: {
          error_code: err?.data?.error_code || "login_failed",
          requires_verification: !!err?.data?.requires_verification,
          method: "password",
        },
      });
      if (err?.data?.requires_verification) {
        setVerificationEmail(err?.data?.email || email.trim());
        setVerificationNotice(t.verify_required_login);
        setInlineError("");
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t.login}
      intro={verificationEmail ? t.verify_intro : t.login_intro}
      testid="login-page"
      footer={
        <>
          <Link to="/forgot-password" className="font-medium text-[#F37021] hover:underline">{t.forgot}</Link>
          <span className="mx-2 text-[#c8cdd6]">•</span>
          <span>{t.no_account} <Link to="/signup" className="font-medium text-[#F37021] hover:underline">{t.signup}</Link></span>
        </>
      }
    >
      {verificationEmail ? (
        <VerificationStep
          email={verificationEmail}
          initialNotice={verificationNotice}
          onBack={() => setVerificationEmail("")}
          backLabel={t.back_to_login}
        />
      ) : (
        <form onSubmit={onPasswordSubmit} className="space-y-4">
          <Input icon={Mail} label={t.email_or_phone || t.email} type="text" autoComplete="username" required value={email} onChange={(e) => { setEmail(e.target.value); if (inlineError) setInlineError(""); }} placeholder={t.email_or_phone_placeholder || t.email} data-testid="login-email" />
          <PasswordInput icon={Lock} label={t.password} autoComplete="current-password" required value={password} onChange={(e) => { setPassword(e.target.value); if (inlineError) setInlineError(""); }} placeholder={t.password} data-testid="login-password" showLabel={t.show_password} hideLabel={t.hide_password} toggleTestId="login-password-toggle" />
          {inlineError && (
            <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-relaxed text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200" data-testid="login-inline-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}
          <PrimaryBtn loading={loading} data-testid="login-submit">{t.submit_login}</PrimaryBtn>
        </form>
      )}
    </AuthShell>
  );
}

export function SignupPage() {
  const t = useT();
  const navigate = useNavigate();
  const { apiPublic, formatApiDetail } = useCustomerAuth();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationNotice, setVerificationNotice] = useState("");
  const passwordChecks = resetPasswordChecks(form.password);
  const passwordValid = allPasswordChecksPass(passwordChecks);
  const showPasswordHelp = form.password.length > 0;

  useEffect(() => {
    trackEvent("signup_view", { routeName: "signup" });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid) return toast.error(t.pwd_requirements);
    setLoading(true);
    try {
      const res = await apiPublic("/signup", { method: "POST", body: JSON.stringify(form) });
      const data = await requestAuthPayload(res, formatApiDetail, t.signup_failed);
      if (data?.requires_verification) {
        trackEvent("customer_signup_success", {
          routeName: "signup",
          customerId: data?.customer?.id,
          metadata: { requires_verification: true },
        });
        trackEvent("email_verification_requested", {
          routeName: "signup",
          customerId: data?.customer?.id,
          metadata: { source: "signup", email_sent: data.email_sent !== false },
        });
        setVerificationEmail(data.email || form.email.trim());
        setVerificationNotice(data.email_sent ? t.verify_sent : t.verify_send_failed);
        if (data.email_sent) toast.success(t.verify_sent);
        else toast.error(t.verify_send_failed);
        return;
      }
      trackEvent("customer_signup_success", {
        routeName: "signup",
        customerId: data?.customer?.id,
        metadata: { requires_verification: false },
      });
      toast.success("Account created");
      navigate("/account");
    } catch (err) {
      trackEvent("customer_signup_failed", {
        routeName: "signup",
        metadata: { error_code: err?.data?.error_code || "signup_failed" },
      });
      toast.error(friendlyAuthError(err, t, t.signup_failed));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t.signup}
      intro={verificationEmail ? t.verify_intro : t.signup_intro}
      testid="signup-page"
      footer={<>{t.have_account} <Link to="/login" className="font-medium text-[#F37021] hover:underline">{t.login}</Link></>}
    >
      {verificationEmail ? (
        <VerificationStep
          email={verificationEmail}
          initialNotice={verificationNotice}
          onBack={() => setVerificationEmail("")}
          backLabel={t.back_to_signup}
        />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Input icon={User} label={t.name} required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder={t.name} data-testid="signup-name" />
          <Input icon={Mail} label={t.email} type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.email} data-testid="signup-email" />
          <Input icon={Phone} label={t.phone} type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.phone_placeholder} hint={t.phone_hint} data-testid="signup-phone" />
          <PasswordInput
            icon={Lock}
            label={t.password}
            hint={t.pwd_requirements}
            autoComplete="new-password"
            required
            minLength={8}
            aria-invalid={showPasswordHelp && !passwordValid}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={t.password}
            data-testid="signup-password"
            showLabel={t.show_password}
            hideLabel={t.hide_password}
            toggleTestId="signup-password-toggle"
          />
          <div className="grid gap-1.5 rounded-[24px] border border-black/6 bg-[#fbfcfe] p-4 text-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
            {[
              ["length", t.pwd_len_check],
              ["uppercase", t.pwd_upper_check],
              ["special", t.pwd_special_check],
            ].map(([key, label]) => (
              <div key={key} className={`flex items-center gap-2 ${passwordChecks[key] ? "text-emerald-600" : "text-[#777]"}`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${passwordChecks[key] ? "bg-emerald-100" : "bg-black/5"}`}>
                  {passwordChecks[key] ? "✓" : "•"}
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>
          <PrimaryBtn loading={loading} disabled={!passwordValid} data-testid="signup-submit">{t.submit_signup}</PrimaryBtn>
        </form>
      )}
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const t = useT();
  const { apiPublic } = useCustomerAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiPublic("/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
      trackEvent("password_reset_requested", {
        routeName: "forgot_password",
      });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t.forgot}
      intro={t.forgot_intro}
      testid="forgot-page"
      footer={<>{t.remember_pwd} <Link to="/login" className="font-medium text-[#F37021] hover:underline">{t.login}</Link></>}
    >
      {sent ? (
        <div className="rounded-[28px] border border-emerald-100 bg-[linear-gradient(180deg,#f8fffb_0%,#effaf4_100%)] px-5 py-6 text-center shadow-[0_16px_36px_rgba(16,24,40,0.08)]" data-testid="forgot-sent">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="text-sm leading-7 text-[#445064]">{t.forgot_sent}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Input icon={Mail} label={t.email} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.email} data-testid="forgot-email" />
          <PrimaryBtn loading={loading} data-testid="forgot-submit">{t.submit_forgot}</PrimaryBtn>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { apiPublic, formatApiDetail } = useCustomerAuth();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenMissing, setTokenMissing] = useState(false);
  const [inlineError, setInlineError] = useState("");

  useEffect(() => {
    const tk = (params.get("token") || "").trim();
    if (!tk) {
      setToken("");
      setTokenMissing(true);
      setInlineError(t.missing_token);
    } else {
      setToken(tk);
      setTokenMissing(false);
      setInlineError("");
    }
  }, [params, t.missing_token]);

  const checks = resetPasswordChecks(password);
  const passwordValid = allPasswordChecksPass(checks);
  const confirmStarted = confirm.length > 0;
  const confirmMatches = confirmStarted && password === confirm;
  const canSubmit = Boolean(token) && passwordValid && confirmMatches && !loading && !done;
  const showPasswordWeakMessage = password.length > 0 && !passwordValid;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setInlineError(t.missing_token);
      return;
    }
    if (!passwordValid) {
      setInlineError(t.pwd_requirements);
      return;
    }
    if (password !== confirm) {
      setInlineError(t.pwd_mismatch);
      return;
    }

    setInlineError("");
    setLoading(true);
    try {
      const res = await apiPublic("/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
      await requestAuthPayload(res, formatApiDetail, t.reset_failed);
      setDone(true);
      toast.success(t.reset_ok);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const message = friendlyAuthError(err, t, t.reset_failed);
      setInlineError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t.reset}
      intro={t.reset_intro}
      testid="reset-page"
      footer={<Link to="/login" className="font-medium text-[#F37021] hover:underline">{t.back_to_login}</Link>}
    >
      {tokenMissing ? (
        <div className="rounded-[28px] border border-red-200 bg-[linear-gradient(180deg,#fff9f8_0%,#fff1ef_100%)] px-5 py-6 text-center text-sm leading-7 text-red-700 shadow-[0_16px_36px_rgba(16,24,40,0.08)]" data-testid="reset-token-missing">
          {t.missing_token}
        </div>
      ) : done ? (
        <div className="rounded-[28px] border border-emerald-100 bg-[linear-gradient(180deg,#f8fffb_0%,#effaf4_100%)] px-5 py-6 text-center shadow-[0_16px_36px_rgba(16,24,40,0.08)]" data-testid="reset-done">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="text-sm leading-7 text-[#445064]">{t.reset_ok}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <PasswordInput
            icon={Lock}
            label={t.password}
            hint={t.pwd_requirements}
            autoComplete="new-password"
            required
            minLength={8}
            aria-invalid={showPasswordWeakMessage}
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (inlineError) setInlineError(""); }}
            placeholder={t.password}
            data-testid="reset-password"
            showLabel={t.show_password}
            hideLabel={t.hide_password}
            toggleTestId="reset-password-toggle"
          />

          <div className="grid gap-1.5 rounded-[24px] border border-black/6 bg-[#fbfcfe] p-4 text-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
            {[
              ["length", t.pwd_len_check],
              ["uppercase", t.pwd_upper_check],
              ["special", t.pwd_special_check],
            ].map(([key, label]) => (
              <div key={key} className={`flex items-center gap-2 ${checks[key] ? "text-emerald-600" : "text-[#777]"}`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${checks[key] ? "bg-emerald-100" : "bg-black/5"}`}>
                  {checks[key] ? "✓" : "•"}
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {showPasswordWeakMessage && (
            <p className="text-xs text-red-600" data-testid="reset-password-error">{t.pwd_requirements}</p>
          )}

          <PasswordInput
            icon={Lock}
            label={t.confirm}
            autoComplete="new-password"
            required
            minLength={8}
            aria-invalid={confirmStarted && !confirmMatches}
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); if (inlineError) setInlineError(""); }}
            placeholder={t.confirm}
            data-testid="reset-confirm"
            showLabel={t.show_password}
            hideLabel={t.hide_password}
            toggleTestId="reset-confirm-toggle"
          />

          {confirmStarted && !confirmMatches && (
            <p className="text-xs text-red-600" data-testid="reset-confirm-error">{t.pwd_mismatch}</p>
          )}

          {inlineError && (
            <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-relaxed text-red-700" data-testid="reset-inline-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}

          <PrimaryBtn loading={loading} disabled={!canSubmit} data-testid="reset-submit">{t.submit_reset}</PrimaryBtn>
        </form>
      )}
    </AuthShell>
  );
}
